import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchPublicEntryById } from '../lib/supabase';

const TYPE_ICONS = {
  image: '🖼', video: '🎬', text: '📄',
  longform: '📝', code: '💻', prompt: '✦',
};

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} style={{
      display: 'flex', alignItems: 'center', gap: 6,
      background: copied ? 'rgba(29,158,117,0.12)' : 'var(--surface2)',
      border: `0.5px solid ${copied ? 'rgba(29,158,117,0.4)' : 'var(--border2)'}`,
      borderRadius: 8, padding: '9px 16px',
      fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500,
      color: copied ? '#5dcaa5' : 'var(--text)', cursor: 'pointer',
      transition: 'all 0.15s',
    }}>
      {copied ? '✓ Copied' : `📋 ${label}`}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--dim)', fontWeight: 500, marginBottom: 8 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function CodeBlock({ text }) {
  return (
    <div style={{
      background: 'var(--surface2)', border: '0.5px solid var(--border)',
      borderRadius: 10, padding: '14px 16px',
      fontSize: 13.5, color: 'var(--text)', lineHeight: 1.75,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      fontFamily: 'var(--font)', maxHeight: 360, overflowY: 'auto',
    }}>
      {text}
    </div>
  );
}

export default function EntryPage() {
  const { id } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    if (!id) { setNotFound(true); setLoading(false); return; }
    fetchPublicEntryById(id)
      .then(data => {
        if (!data) setNotFound(true);
        else setEntry(data);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const shareUrl = `${window.location.origin}/entry/${id}`;

  if (loading) return (
    <div style={s.page}>
      <Nav />
      <div style={s.center}>
        <div style={s.spinner} />
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 12 }}>Loading entry...</div>
      </div>
    </div>
  );

  if (notFound) return (
    <div style={s.page}>
      <Nav />
      <div style={s.center}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--text)', marginBottom: 8 }}>Entry not found</div>
        <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 24, textAlign: 'center', lineHeight: 1.6, maxWidth: 360 }}>
          This entry doesn't exist, has been made private, or the link may be incorrect.
        </div>
        <Link to="/app" style={s.btn}>Browse public prompts →</Link>
      </div>
    </div>
  );

  const promptPreview = entry.prompt?.length > 400 && !showFull
    ? entry.prompt.slice(0, 400) + '…'
    : entry.prompt;

  return (
    <div style={s.page}>
      <Nav />

      <div style={s.container}>
        {/* Header */}
        <div style={s.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 20 }}>{TYPE_ICONS[entry.type] || '✦'}</span>
            <h1 style={s.title}>{entry.title}</h1>
          </div>
          <div style={s.meta}>
            {entry.profiles?.username && (
              <span>by <strong style={{ color: 'var(--text)' }}>@{entry.profiles.username}</strong></span>
            )}
            {entry.model && <span>· {entry.model}</span>}
            {entry.type && (
              <span style={{
                padding: '2px 8px', borderRadius: 10, fontSize: 11,
                background: 'var(--surface2)', border: '0.5px solid var(--border)',
                color: 'var(--muted)',
              }}>{entry.type}</span>
            )}
            <span>· {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          </div>
        </div>

        <div style={s.grid}>
          {/* Main content */}
          <div style={s.main}>

            {/* Prompt */}
            {entry.prompt && (
              <Field label="Prompt">
                <CodeBlock text={promptPreview} />
                {entry.prompt.length > 400 && (
                  <button onClick={() => setShowFull(v => !v)} style={s.showMore}>
                    {showFull ? '▲ Show less' : '▼ Show full prompt'}
                  </button>
                )}
              </Field>
            )}

            {/* Output */}
            {entry.text_output && (
              <Field label="Output">
                <CodeBlock text={entry.text_output} />
              </Field>
            )}

            {/* Notes */}
            {entry.notes && (
              <Field label="Notes">
                <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7 }}>{entry.notes}</div>
              </Field>
            )}

            {/* Source URL */}
            {entry.source_url && (
              <Field label="Source">
                <a href={entry.source_url} target="_blank" rel="noreferrer"
                  style={{ fontSize: 13, color: 'var(--accent)', wordBreak: 'break-all' }}>
                  {entry.source_url}
                </a>
              </Field>
            )}
          </div>

          {/* Sidebar actions */}
          <div style={s.sidebar}>
            <div style={s.actionCard}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>Use this prompt</div>
              <div style={{ fontSize: 12, color: 'var(--dim)', lineHeight: 1.6, marginBottom: 14 }}>
                Copy the prompt and paste it into any AI tool.
              </div>
              <CopyButton text={entry.prompt} label="Copy prompt" />
              {entry.text_output && (
                <div style={{ marginTop: 8 }}>
                  <CopyButton text={entry.text_output} label="Copy output" />
                </div>
              )}
            </div>

            {/* Save to library CTA */}
            <div style={{ ...s.actionCard, background: 'rgba(127,119,221,0.06)', borderColor: 'rgba(127,119,221,0.3)' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#7f77dd', marginBottom: 4 }}>Save to your library</div>
              <div style={{ fontSize: 12, color: 'var(--dim)', lineHeight: 1.6, marginBottom: 14 }}>
                Create a free account to save and organize prompts like this one.
              </div>
              <Link to="/auth?tab=signup" style={{ ...s.btn, display: 'block', textAlign: 'center', textDecoration: 'none' }}>
                Sign up free →
              </Link>
            </div>

            {/* Share */}
            <div style={s.actionCard}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>Share this prompt</div>
              <div style={{ fontSize: 11, color: 'var(--dim)', wordBreak: 'break-all', lineHeight: 1.5, marginBottom: 10, background: 'var(--surface2)', padding: '8px 10px', borderRadius: 6 }}>
                {shareUrl}
              </div>
              <CopyButton text={shareUrl} label="Copy link" />
            </div>

            {/* Tags */}
            {entry.tags?.length > 0 && (
              <div>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--dim)', marginBottom: 8 }}>Tags</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {entry.tags.map(tag => (
                    <span key={tag} style={{
                      fontSize: 11, padding: '3px 9px', borderRadius: 10,
                      background: 'var(--surface2)', border: '0.5px solid var(--border)',
                      color: 'var(--muted)',
                    }}>{tag}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={s.footer}>
        <div style={{ fontSize: 12, color: 'var(--dim)' }}>
          © {new Date().getFullYear()} CopyPasteGenius ·{' '}
          <Link to="/" style={{ color: 'var(--dim)', textDecoration: 'none' }}>Browse all prompts</Link>
          {' · '}
          <Link to="/auth" style={{ color: 'var(--dim)', textDecoration: 'none' }}>Sign in</Link>
        </div>
      </footer>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 680px) {
          .entry-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function Nav() {
  return (
    <div style={{
      background: 'var(--surface)', borderBottom: '0.5px solid var(--border)',
      padding: '0 24px', height: 58, display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', fontFamily: 'var(--font)',
    }}>
      <Link to="/" style={{ fontSize: 16, fontWeight: 500, color: 'var(--text)', textDecoration: 'none', letterSpacing: '-0.03em' }}>
        CopyPaste<span style={{ color: '#7f77dd' }}>Genius</span>
      </Link>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Link to="/app" style={{ fontSize: 13, color: 'var(--muted)', textDecoration: 'none' }}>Browse prompts</Link>
        <Link to="/auth" style={{
          fontSize: 13, fontWeight: 500, color: '#fff',
          background: '#7f77dd', borderRadius: 8, padding: '7px 14px',
          textDecoration: 'none',
        }}>Sign up free</Link>
      </div>
    </div>
  );
}

const s = {
  page:      { minHeight: '100vh', background: 'var(--bg)', fontFamily: 'var(--font)', display: 'flex', flexDirection: 'column' },
  center:    { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 },
  spinner:   { width: 32, height: 32, borderRadius: '50%', border: '2.5px solid var(--border2)', borderTopColor: '#7f77dd', animation: 'spin 0.7s linear infinite' },
  container: { maxWidth: 960, margin: '0 auto', padding: '36px 24px', flex: 1, width: '100%' },
  header:    { marginBottom: 32 },
  title:     { fontSize: 24, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em', lineHeight: 1.3 },
  meta:      { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13, color: 'var(--dim)', marginTop: 6 },
  grid:      { display: 'grid', gridTemplateColumns: 'clamp(200px, 60%, 1fr) 280px', gap: 32, alignItems: 'flex-start' },
  main:      {},
  sidebar:   { display: 'flex', flexDirection: 'column', gap: 16 },
  actionCard:{ background: 'var(--surface)', border: '0.5px solid var(--border2)', borderRadius: 12, padding: '16px' },
  showMore:  { background: 'none', border: 'none', color: 'var(--accent)', fontSize: 12, cursor: 'pointer', padding: '6px 0', fontFamily: 'var(--font)' },
  btn:       { background: '#7f77dd', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  footer:    { borderTop: '0.5px solid var(--border)', padding: '20px 24px', textAlign: 'center', background: 'var(--surface)' },
};